import {
	AdverseEventContactSchema,
	type AdverseEventContactEntry,
} from '#app/routes/mocra+/_form+/adverse-events/form.ts'
import { sanitizeContactReferenceDraft } from '#app/routes/mocra+/_form+/components/contact-references/definition.ts'
import {
	createEmptyContactDraft,
	type ContactDraft,
} from '#app/routes/mocra+/_form+/components/contacts/types.ts'
import { assertEntityDefinition } from '#app/routes/mocra+/_form+/entity-definition.ts'

type Options = {
	readonly defaultRole: string
}

export function createAdverseEventContactDefinition(options: Options) {
	const { defaultRole } = options
	return assertEntityDefinition<AdverseEventContactEntry, ContactDraft>({
		schema: AdverseEventContactSchema,
		createDraft: () => createEmptyContactDraft(),
		sanitizeDraft: (draft) =>
			sanitizeAdverseEventContactDraft(draft, defaultRole),
		serialize: (draft) => draftToEntry(draft, defaultRole),
		deserialize: (entry) => entryToDraft(entry, defaultRole),
		collectErrors: (draft) => collectDraftErrors(draft),
	})
}

function sanitizeAdverseEventContactDraft(
	draft: ContactDraft,
	defaultRole: string,
): ContactDraft {
	return sanitizeContactReferenceDraft(draft, defaultRole)
}

function draftToEntry(
	draft: ContactDraft,
	defaultRole: string,
): AdverseEventContactEntry {
	const sanitized = sanitizeAdverseEventContactDraft(draft, defaultRole)
	const id = sanitized.id?.trim()
	if (!id) {
		throw new Error('Adverse event contacts require a stable contact id.')
	}
	const role = sanitized.title || defaultRole
	const name = sanitized.name
	const email = sanitized.email
	const phone = sanitized.phone ?? ''

	return {
		role,
		name,
		email,
		phone,
		contactId: id,
		fallbackName: name || role,
		fallbackEmail: email,
		fallbackTitle: role,
		fallbackPhone: phone || undefined,
	}
}

function entryToDraft(
	entry: AdverseEventContactEntry,
	defaultRole: string,
): ContactDraft {
	const normalized = normalizeEntry(entry, defaultRole)
	return {
		id: normalized.contactId,
		title: normalized.role,
		name: normalized.name,
		email: normalized.email,
		phone: normalized.phone ?? '',
	}
}

function collectDraftErrors(draft: ContactDraft): readonly string[] {
	const messages = new Set<string>()
	if (!draft.id || draft.id.trim().length === 0) {
		messages.add('Select a contact from the directory.')
	}
	if (!draft.name || draft.name.trim().length === 0) {
		messages.add('Enter the contact name.')
	}
	if (!draft.email || draft.email.trim().length === 0) {
		messages.add('Enter the contact email.')
	}
	return [...messages]
}

function normalizeEntry(
	entry: AdverseEventContactEntry,
	defaultRole: string,
): AdverseEventContactEntry {
	const role =
		toOptionalString(entry.role) ??
		toOptionalString(entry.fallbackTitle) ??
		defaultRole
	const name =
		toOptionalString(entry.name) ?? toOptionalString(entry.fallbackName) ?? role
	const email =
		toOptionalString(entry.email) ?? toOptionalString(entry.fallbackEmail) ?? ''
	const phone =
		toOptionalString(entry.phone) ?? toOptionalString(entry.fallbackPhone) ?? ''
	const contactId = toOptionalString(entry.contactId) ?? ''

	return {
		role,
		name,
		email,
		phone,
		contactId,
		fallbackName: name || undefined,
		fallbackEmail: email || undefined,
		fallbackTitle: role || undefined,
		fallbackPhone: phone || undefined,
	}
}

function toOptionalString(value: unknown): string | undefined {
	if (typeof value !== 'string') return undefined
	const trimmed = value.trim()
	return trimmed.length > 0 ? trimmed : undefined
}
